"use client"

import { useEffect, useRef } from "react"
import { usePathname, useRouter } from "next/navigation"
import { toast } from "sonner"

import {
  canInterceptBack,
  hasNavigationApi,
  isSafeBackPlatform,
} from "@/lib/back-intercept"
import { goHome, HOME } from "@/lib/back-nav"
import {
  getSuppressNextPop,
  peekOverlay,
  popOverlay,
  setSuppressNextPop,
} from "@/lib/overlay-stack"
import { useT } from "@/lib/i18n"
import { useHydrated } from "@/lib/use-hydrated"

/**
 * Native-style back-button controller. Null-rendering; mounted once in
 * AppProviders (same precedent as CompleteDataGuard).
 *
 * Android (intercept mode): a same-URL `pushState` sentinel guards ONLY the
 * routes whose back must be intercepted — `/home` (double-back-to-exit) and a
 * deep-link launch floor (so a shortcut-launched page resolves back to Home).
 * Tab-flattening and parent-back come for free from BottomNav's replace
 * discipline (Home always sits directly beneath the active tab) — so sub-pages
 * are NEVER armed and their back is fully natural.
 *
 * The back decision reads the LANDED history entry's own state (sentinel or
 * not) — a synchronous DOM read, independent of React re-render timing:
 *  - overlay open            → close it, no navigation
 *  - landed ON a sentinel    → we backed onto a guarded root from a deeper page
 *                              → stay (natural arrival)
 *  - consumed a sentinel +
 *    landed on /home          → double-back-to-exit
 *    landed on launch floor   → flatten to Home
 *  - anything else            → a normal sub-page/tab back → do nothing
 *
 * iOS (observe mode, 26.2+): the swipe-back can't be intercepted, but the
 * Navigation API's `navigate` event lets us OBSERVE a traverse and close an open
 * overlay best-effort. Flattening on iOS comes entirely from the replace
 * discipline.
 */
export function BackButtonController() {
  const router = useRouter()
  const pathname = usePathname()
  const hydrated = useHydrated()
  const t = useT()

  // Latest values for the long-lived listeners, without re-subscribing.
  const latest = useRef({ router, t })
  useEffect(() => {
    latest.current = { router, t }
  })

  const exitArmedUntil = useRef(0)
  const launchFloor = useRef<string | null>(null)
  const didInit = useRef(false)

  const intercept = hydrated && canInterceptBack()
  const observe =
    hydrated && !intercept && isSafeBackPlatform() && hasNavigationApi()

  // --- Arm the sentinel on guarded routes (Android intercept mode) ---
  useEffect(() => {
    if (!intercept) return

    // One-time: a deep-link / shortcut cold launch (single history entry into
    // anything but Home/auth, e.g. /book, /check-in, /membership-card,
    // /bookings/:id) gets a back-to-Home floor so its back resolves to Home
    // instead of exiting the app.
    if (!didInit.current) {
      didInit.current = true
      const fresh = window.history.length <= 1
      if (fresh && pathname !== HOME && !isAuthRoute(pathname)) {
        launchFloor.current = pathname
      }
    }

    exitArmedUntil.current = 0
    if (needsSentinel(pathname, launchFloor.current) && !hasSentinel())
      pushSentinel()
  }, [intercept, pathname])

  // --- popstate handler (Android intercept mode) ---
  useEffect(() => {
    if (!intercept) return

    function onPop() {
      if (getSuppressNextPop()) {
        setSuppressNextPop(false)
        return
      }

      // (1) Overlay first — a back with a sheet/dialog open closes it, never
      // navigates or exits.
      if (peekOverlay()) {
        popOverlay()?.onClose()
        return
      }

      // (2) Landed on a guard sentinel = backed onto a guarded root (Home /
      // launch floor) from a deeper page → natural arrival, stay put.
      if (hasSentinel()) return

      // (3) We consumed a guard sentinel: back was pressed while ON that root.
      const landed = window.location.pathname

      if (landed === HOME) {
        const now = Date.now()
        if (now < exitArmedUntil.current) {
          // 2nd press within the window: let it go. With Home at the history
          // root the OS closes the PWA; this only runs in the rare "junk
          // beneath Home" case, where we simply allow the navigation.
          exitArmedUntil.current = 0
          return
        }
        exitArmedUntil.current = now + EXIT_WINDOW_MS
        toast(latest.current.t("common.back.exitHint"))
        // Stay on the root Home entry (don't re-push): the next OS back closes
        // the app. If the user doesn't press again, re-protect after the window.
        window.setTimeout(() => {
          if (
            Date.now() >= exitArmedUntil.current &&
            window.location.pathname === HOME &&
            !hasSentinel()
          ) {
            pushSentinel()
            exitArmedUntil.current = 0
          }
        }, EXIT_WINDOW_MS + 50)
        return
      }

      if (landed === launchFloor.current) {
        goHome(latest.current.router)
        return
      }

      // Any other landing = a normal sub-page / tab back → fully natural.
    }

    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [intercept])

  // --- iOS 26.2+ best-effort observation: close an open overlay on swipe-back ---
  useEffect(() => {
    if (!observe) return

    const nav = (window as unknown as { navigation: EventTarget }).navigation

    function onNavigate(event: Event) {
      if ((event as { navigationType?: string }).navigationType !== "traverse")
        return
      if (getSuppressNextPop()) {
        setSuppressNextPop(false)
        return
      }
      if (peekOverlay()) popOverlay()?.onClose()
    }

    nav.addEventListener("navigate", onNavigate)
    return () => nav.removeEventListener("navigate", onNavigate)
  }, [observe])

  return null
}

const EXIT_WINDOW_MS = 2000

// Auth / onboarding / splash — never guarded; default back behavior.
const AUTH_PREFIXES = [
  "/sign-in",
  "/sign-up",
  "/forgot-password",
  "/otp",
  "/set-password",
  "/complete-data",
  "/profile-setup",
  "/tutorial",
]

function isAuthRoute(p: string): boolean {
  return (
    p === "/" || AUTH_PREFIXES.some((a) => p === a || p.startsWith(a + "/"))
  )
}

// Only Home (exit trap) and a deep-link launch floor get a sentinel.
function needsSentinel(p: string, floor: string | null): boolean {
  return p === HOME || p === floor
}

function hasSentinel(): boolean {
  return (
    (window.history.state as { __psc?: string } | null)?.__psc === "sentinel"
  )
}

function pushSentinel(): void {
  const current = (window.history.state as object) ?? {}
  window.history.pushState(
    { ...current, __psc: "sentinel" },
    "",
    window.location.href
  )
}
